import { DeleteOutline } from "@mui/icons-material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolderOutlined";
import Description from "@mui/icons-material/Description";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import LockIcon from "@mui/icons-material/Lock";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleIcon from "@mui/icons-material/People";
import PostAddIcon from "@mui/icons-material/PostAddOutlined";
import PublicIcon from "@mui/icons-material/Public";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SourceIcon from "@mui/icons-material/SourceOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import TravelExploreIcon from "@mui/icons-material/TravelExploreOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import FolderSelector from "../../components/FolderSelector";
import SelectEmployeeDialog from "../../components/SelectEmployee";
import { ALLOWED_DOCUMENT_EXTENSIONS } from "../../constants";
import { Company, Document, Employee } from "../../types";
import { formatTimestamp, hostname, parseError, uniqueId } from "../../utils";
import { hasAdminAccessByRole } from "../../utils/roleUtils";
import { isFreeUser } from "../../utils/subscriptionUtils";
import { useEmployeeTerminology } from "../../utils/terminology";
import { useFeatures } from "../../context/FeatureContext";
import { FEATURES } from "../../constants/features";
import WebsiteCrawlerDialog from "../integrations/WebsiteCrawlerDialog";
import CreateDocumentDialog from "./dialogs/CreateDocumentDialog";
import CreateFolderDialog from "./dialogs/CreateFolderDialog";
import EditDocumentDialog from "./dialogs/EditDocumentDialog";
import EditFolderTagsDialog from "./dialogs/EditFolderTagsDialog";
import ManageTagsDialog from "./dialogs/ManageTagsDialog";
import UploadDocumentDialog from "./dialogs/UploadDocumentDialog";
import SharedFoldersDialog from "./dialogs/SharedFoldersDialog";
import ShareFolderDialog from "./dialogs/ShareFolderDialog";
import RenameFolderDialog from "./dialogs/RenameFolderDialog";
import TransferFolderDialog from "./dialogs/TransferFolderDialog";

// Button component to show shared folders count
interface SharedFoldersButtonProps {
  companyId: string;
  getToken: () => Promise<string>;
  onClick: () => void;
  refreshKey: number;
  disabled?: boolean;
}

const SharedFoldersButton: React.FC<SharedFoldersButtonProps> = ({
  companyId,
  getToken,
  onClick,
  refreshKey,
  disabled,
}) => {
  const [outgoingCount, setOutgoingCount] = useState<number>(0);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [hasPublicShare, setHasPublicShare] = useState<boolean>(false);

  useEffect(() => {
    loadCounts();
  }, [companyId, refreshKey]);

  const loadCounts = async () => {
    try {
      const token = await getToken();

      // Get outgoing shares count
      const outgoingResponse = await axios.get(
        `${hostname}/companies/${companyId}/folder-shares/outgoing`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const shares = outgoingResponse.data.shares || [];
      setOutgoingCount(shares.length);
      setHasPublicShare(shares.some((s: any) => s.shareType === "public"));

      // Get pending invitations count
      const pendingResponse = await axios.get(
        `${hostname}/companies/${companyId}/share-invitations/pending`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setPendingCount(pendingResponse.data.invitations?.length || 0);
    } catch (error) {
      console.error("Error loading shared folders count:", error);
    }
  };

  const hasShares = outgoingCount > 0;
  const hasPending = pendingCount > 0;

  return (
    <Button
      variant="outlined"
      onClick={onClick}
      disabled={disabled}
      color={hasPending ? "error" : hasPublicShare ? "warning" : "primary"}
      sx={{
        minWidth: { xs: "100%", sm: "auto" },
      }}
    >
      {hasPublicShare ? (
        <PublicIcon style={{ marginRight: 3 }} />
      ) : (
        <FolderSharedIcon style={{ marginRight: 3 }} />
      )}
      Shared Folders
      {(hasShares || hasPending) && ` (${outgoingCount}${hasPending ? ` +${pendingCount}` : ""})`}
    </Button>
  );
};

interface FileSystemEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
}

interface FileSystemFileEntry extends FileSystemEntry {
  file(successCallback: (file: File) => void): void;
}

interface FileSystemDirectoryEntry extends FileSystemEntry {
  createReader(): FileSystemDirectoryReader;
}

interface FileSystemDirectoryReader {
  readEntries(successCallback: (entries: FileSystemEntry[]) => void): void;
}

interface DataTransferItem {
  webkitGetAsEntry?(): FileSystemEntry | null;
}

// Helper function to get sharing status icon, color, and tooltip
const getSharingChipProps = (shareVisibility: string | undefined | null, legacySharing?: string, isExternal?: boolean) => {
  // Check for external folders (public folders from other companies)
  if (isExternal || legacySharing === "External") {
    return {
      icon: <TravelExploreIcon />,
      color: "warning" as const,
      label: "External",
      tooltip: "Public folder from another company",
    };
  }

  // Check new shareVisibility first
  switch (shareVisibility) {
    case "public":
      return {
        icon: <PublicIcon />,
        color: "warning" as const,
        label: "Public",
        tooltip: "Available to other companies on the platform",
      };
    case "direct":
      return {
        icon: <FolderSharedIcon />,
        color: "primary" as const,
        label: "Shared",
        tooltip: "Shared with specific companies",
      };
    case "company":
      return {
        icon: <PeopleIcon />,
        color: "primary" as const,
        label: "Shared",
        tooltip: "Shared with company",
      };
  }

  // Fall back to legacy sharing field for special folders like "Shared"
  if (legacySharing === "Shared") {
    return {
      icon: <PeopleIcon />,
      color: "primary" as const,
      label: "Shared",
      tooltip: "Shared with company",
    };
  }

  return {
    icon: <LockIcon />,
    color: "default" as const,
    label: "Private",
    tooltip: "Private to the current user only",
  };
};

export default ({
  currentUser,
  companyId,
  token,
  currentPlan,
  getToken,
  eventListener,
  openDialog,
  documentId,
  setTab,
  company,
  employees,
  userRole,
}: {
  currentUser: any | User;
  currentPlan: any | string;
  companyId: string;
  token: any | string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  openDialog: (
    title: string,
    content: string,
    buttonText: string,
    onClick: () => void
  ) => void;
  documentId: any | string;
  setTab: (tab: string) => void;
  company: Company | any;
  employees: Array<Employee | any>;
  userRole?: string | null;
}) => {
  const {
    employee: employeeText,
    employees: employeesText,
    employeeLower,
  } = useEmployeeTerminology();
  const { isFeatureHidden } = useFeatures();

  // Check if user has admin access for public folder management
  const isAdmin = hasAdminAccessByRole(userRole ?? null);
  const getNewDocument = () => {
    return {
      id: uniqueId(),
      companyId: companyId,
      name: "",
      type: "txt",
      content: "",
      tags: [],
      tokens: 0,
      created: "",
      updated: "",
      subDocuments: [],
      path: "",
    };
  };
  const [init, setInit] = useState(false);
  const [documents, setDocuments] = useState<Array<Document>>([]);
  const [tags, setTags] = useState<Array<string>>([]);
  const [newDocument, setNewDocument] = useState<Document>(getNewDocument());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [path, setPath] = useState("/");
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [createDocumentOpen, setCreateDocumentOpen] = useState(false);
  const [editDocumentOpen, setEditDocumentOpen] = useState(false);
  const [uploadDocumentOpen, setUploadDocumentOpen] = useState(false);
  const [crawlWebsiteOpen, setCrawlWebsiteOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [documentContentLoading, setDocumentContentLoading] = useState(false);
  const [selectEmployeesOpen, setSelectEmployeesOpen] = useState(false);
  const [openButton, setOpenButton] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [folderSelectorOpen, setFolderSelectorOpen] = useState(false);
  const [moveFileId, setMoveFileId] = useState("");
  const [copyFileId, setCopyFileId] = useState("");
  const [isMovingFile, setIsMovingFile] = useState(true);
  const [moveFolderId, setMoveFolderId] = useState("");
  const [dialogLoading, setDialogLoading] = useState(false);
  const [folderOperationLoading, setFolderOperationLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const [editFolderTagsOpen, setEditFolderTagsOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<Document | null>(null);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [shareFolderOpen, setShareFolderOpen] = useState(false);
  const [sharedFoldersDialogOpen, setSharedFoldersDialogOpen] = useState(false);
  const [statusBarRefreshKey, setStatusBarRefreshKey] = useState(0);
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [transferFolderOpen, setTransferFolderOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchOptions, setSearchOptions] = useState<Array<Document>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const getRecentlyDeletedData = async () => {
    const currentToken = await getToken();
    try {
      const response = await axios.get(
        `${hostname}/documents/recently-deleted`,
        {
          headers: { Authorization: `Bearer ${currentToken}` },
          params: { companyId: companyId, days: 30 },
        }
      );
      // Ensure we return an array
      return Array.isArray(response.data) ? response.data : [];
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      return [];
    }
  };

  async function getData(folderPath: string) {
    setInit(true);
    setLoading(true);
    const currentToken = await getToken();

    // Handle recently deleted folder
    if (folderPath === "/recently-deleted") {
      const recentlyDeleted = await getRecentlyDeletedData();
      // Ensure documents is always an array
      const documentsArray = Array.isArray(recentlyDeleted)
        ? recentlyDeleted
        : [];
      setDocuments(documentsArray);
      const tgs: Set<string> = new Set();
      documentsArray.forEach((x: Document) => {
        if (x.tags) {
          x.tags.forEach((y: string) => tgs.add(y));
        }
      });
      setTags(Array.from(tgs.values()));
      setLoading(false);
      return;
    }

    await axios
      .get(`${hostname}/documents`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        params: { path: folderPath, companyId: companyId, includeTags: true },
      })
      .then(async (res) => {
        // Ensure documentList is an array
        var documentList = Array.isArray(res.data) ? res.data : [];

        // Fetch outgoing shares for this company to mark folders as shared
        try {
          const sharesResponse = await axios.get(
            `${hostname}/companies/${companyId}/folder-shares/outgoing`,
            {
              headers: { Authorization: `Bearer ${currentToken}` },
            }
          );
          const outgoingShares = sharesResponse.data.shares || [];

          // Create a set of shared folder paths for quick lookup
          const sharedPaths = new Set(
            outgoingShares.map((share: any) => share.folderPath)
          );

          // Mark folders/files as shared if they fall under a shared path
          documentList = documentList.map((doc: Document) => {
            const docPath = doc.path === "/" ? `/${doc.name}` : `${doc.path}/${doc.name}`;

            // Check if this document's path matches or is under any shared path
            const isShared = Array.from(sharedPaths).some((sharedPath: any) => {
              return docPath === sharedPath || docPath.startsWith(sharedPath + "/");
            });

            if (isShared) {
              return { ...doc, shared: true };
            }
            return doc;
          });
        } catch (err) {
          console.error("Error fetching outgoing shares:", err);
          // Continue without marking shared folders
        }

        // Add special folders at root level and ensure proper ordering
        if (folderPath === "/" || folderPath === null) {
          // Separate out the shared folder if it exists
          const sharedFolderIndex = documentList.findIndex(
            (doc) => doc.id === "shared"
          );
          let sharedFolder = null;
          if (sharedFolderIndex !== -1) {
            sharedFolder = documentList.splice(sharedFolderIndex, 1)[0];
          }

          // Add Recently Deleted folder at the beginning
          const recentlyDeletedFolder = {
            id: "recently-deleted",
            name: "Recently Deleted",
            type: "folder",
            tags: [],
          };
          documentList.unshift(recentlyDeletedFolder);

          // Add shared folder as second if it exists
          if (sharedFolder) {
            documentList.splice(1, 0, sharedFolder);
          }
        }

        const tgs: Set<string> = new Set();
        setDocuments(documentList);
        documentList.forEach((x: Document) => {
          if (x.tags) {
            x.tags.forEach((y: string) => tgs.add(y));
          }
        });
        setTags(Array.from(tgs.values()));
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
    setLoading(false);
  }

  const refreshData = async (folderPath: string) => {
    setRefreshing(true);
    const currentToken = await getToken();

    // Handle recently deleted folder
    if (folderPath === "/recently-deleted") {
      const recentlyDeleted = await getRecentlyDeletedData();
      // Ensure documents is always an array
      const documentsArray = Array.isArray(recentlyDeleted)
        ? recentlyDeleted
        : [];
      setDocuments(documentsArray);
      const tgs: Set<string> = new Set();
      documentsArray.forEach((x: Document) => {
        if (x.tags) {
          x.tags.forEach((y: string) => tgs.add(y));
        }
      });
      setTags(Array.from(tgs.values()));
      setRefreshing(false);
      return;
    }

    axios
      .get(`${hostname}/documents`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        params: { path: folderPath, companyId: companyId, includeTags: true },
      })
      .then(async (res) => {
        // Ensure documentList is an array
        var documentList = Array.isArray(res.data) ? res.data : [];

        // Fetch outgoing shares for this company to mark folders as shared
        try {
          const sharesResponse = await axios.get(
            `${hostname}/companies/${companyId}/folder-shares/outgoing`,
            {
              headers: { Authorization: `Bearer ${currentToken}` },
            }
          );
          const outgoingShares = sharesResponse.data.shares || [];

          // Create a set of shared folder paths for quick lookup
          const sharedPaths = new Set(
            outgoingShares.map((share: any) => share.folderPath)
          );

          // Mark folders/files as shared if they fall under a shared path
          documentList = documentList.map((doc: Document) => {
            const docPath = doc.path === "/" ? `/${doc.name}` : `${doc.path}/${doc.name}`;

            // Check if this document's path matches or is under any shared path
            const isShared = Array.from(sharedPaths).some((sharedPath: any) => {
              return docPath === sharedPath || docPath.startsWith(sharedPath + "/");
            });

            if (isShared) {
              return { ...doc, shared: true };
            }
            return doc;
          });
        } catch (err) {
          console.error("Error fetching outgoing shares:", err);
          // Continue without marking shared folders
        }

        // Add special folders at root level and ensure proper ordering
        if (folderPath === "/" || folderPath === null) {
          // Separate out the shared folder if it exists
          const sharedFolderIndex = documentList.findIndex(
            (doc) => doc.id === "shared"
          );
          let sharedFolder = null;
          if (sharedFolderIndex !== -1) {
            sharedFolder = documentList.splice(sharedFolderIndex, 1)[0];
          }

          // Add Recently Deleted folder at the beginning
          const recentlyDeletedFolder = {
            id: "recently-deleted",
            name: "Recently Deleted",
            type: "folder",
            tags: [],
          };
          documentList.unshift(recentlyDeletedFolder);

          // Add shared folder as second if it exists
          if (sharedFolder) {
            documentList.splice(1, 0, sharedFolder);
          }
        }

        const tgs: Set<string> = new Set();
        setDocuments(documentList);
        documentList.forEach((x: Document) => {
          if (x.tags) {
            x.tags.forEach((y: string) => tgs.add(y));
          }
        });
        setTags(Array.from(tgs.values()));
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  const getDocumentContents = async (doc: Document) => {
    setDocumentContentLoading(true);
    const currentToken = await getToken();
    axios
      .get(`${hostname}/documents/${companyId}/${doc.id}`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setNewDocument({ ...doc, content: res.data.content });
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setDocumentContentLoading(false);
      });
  };

  const searchDocuments = async (query: string) => {
    if (!query.trim()) {
      setSearchOptions([]);
      return;
    }

    setIsSearching(true);
    const currentToken = await getToken();

    try {
      const response = await axios.get(`${hostname}/documents/search`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        params: { companyId: companyId, query: query.trim(), limit: 20 },
      });

      const results = response.data.results || [];
      setSearchOptions(results);
    } catch (err) {
      console.error("Search error:", err);
      setSearchOptions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchSelect = (
    _event: React.SyntheticEvent,
    value: string | Document | null
  ) => {
    if (!value || typeof value === "string") return;

    // Navigate to the document's folder path
    const targetPath = value.path || "/";
    setPath(targetPath);
    refreshData(targetPath);

    // If it's a document (not a folder), open it for editing
    if (value.type !== "folder") {
      setNewDocument({ ...value, content: "Loading..." });
      setEditDocumentOpen(true);
      getDocumentContents(value);
    }

    // Clear the search
    setSearchInputValue("");
    setSearchOptions([]);
    setSearchOpen(false);
  };

  const chatWithDocument = async (employee: any) => {
    const currentToken = await getToken();
    const chatId = uuidv4();
    try {
      await axios.post(
        `${hostname}/chats`,
        {
          ...employee,
          id: chatId,
          companyId: companyId,
          ts: new Date().getTime(),
          employeeId: employee.id,
          documentScope: newDocument.id,
          documentPath: newDocument.path,
          folderPath: newDocument.path,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      // Only navigate after chat is successfully created
      navigate("/dashboard/companies/" + companyId + "/chats/" + chatId);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  const openDocumentId = async (dId: string) => {
    const currentToken = await getToken();
    axios
      .get(`${hostname}/documents/${companyId}/document/${dId}`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((res) => {
        setNewDocument({ ...res.data, content: "Loading..." });
        setEditDocumentOpen(true);
        getDocumentContents(res.data);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setDocumentContentLoading(false);
      });
  };

  useEffect(() => {
    if (currentUser.uid) {
      getData(path);
      if (documentId) {
        openDocumentId(documentId);
      }
    }
  }, [currentUser.uid, companyId]);

  const uploadDocumentFile = async (
    file: File,
    tags: string[] = [],
    folderId?: string,
    usePremiumPdf?: boolean
  ) => {
    setDialogLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("tags", tags.join(","));
    formData.append("companyId", companyId);
    formData.append("path", path);
    if (usePremiumPdf) {
      formData.append("premiumPdf", "true");
    }

    const currentToken = await getToken();
    await axios
      .post(`${hostname}/documents/upload?companyId=${companyId}`, formData, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then(() => {
        refreshData(path);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setDialogLoading(false);
      });
  };

  const createDocumentFile = async (document: Partial<Document>) => {
    setDialogLoading(true);
    const formData = new FormData();
    formData.append("content", document.content || "");
    formData.append("name", document.name || "");
    formData.append("tags", (document.tags || []).join(","));
    formData.append("companyId", companyId);
    formData.append("path", path);

    const currentToken = await getToken();
    await axios
      .post(`${hostname}/documents/upload?companyId=${companyId}`, formData, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then(() => {
        refreshData(path);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setDialogLoading(false);
      });
  };

  const deleteDocument = async (d: Document) => {
    setRefreshing(true);
    const currentToken = await getToken();
    await axios
      .delete(`${hostname}/documents/${d.id}?companyId=${companyId}`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
    const newDocuments = documents.filter((x) => x.id !== d.id);
    setDocuments(newDocuments);
    setRefreshing(false);
  };

  const editDocument = async (d: Partial<Document>) => {
    setDialogLoading(true);
    const currentToken = await getToken();
    await axios
      .post(`${hostname}/documents`, d, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then(() => {
        refreshData(path);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setDialogLoading(false);
      });
  };

  const moveFile = async (folder: any) => {
    setFolderOperationLoading(true);
    setRefreshing(true);
    const currentToken = await getToken();
    const endpoint = isMovingFile ? "/documents/move" : "/documents/copy";
    const documentId = isMovingFile ? moveFileId : copyFileId;

    axios
      .post(
        `${hostname}${endpoint}`,
        {
          documentId: documentId,
          companyId: companyId,
          folder: folder,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .then((res) => {
        setFolderSelectorOpen(false);
        refreshData(path);
        eventListener({
          type: "SET_SUCCESS",
          message: isMovingFile
            ? "Document moved successfully"
            : "Document copied successfully",
        });
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setRefreshing(false);
        setFolderOperationLoading(false);
      });
  };

  const moveFolder = async (targetFolder: any) => {
    setFolderOperationLoading(true);
    setRefreshing(true);
    const currentToken = await getToken();

    axios
      .put(
        `${hostname}/documents/folders/${moveFolderId}/move`,
        {
          companyId: companyId,
          folder: targetFolder,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .then((res) => {
        setFolderSelectorOpen(false);
        setMoveFolderId("");
        refreshData(path);
        eventListener({
          type: "SET_SUCCESS",
          message: "Folder moved successfully",
        });
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setRefreshing(false);
        setFolderOperationLoading(false);
      });
  };

  const createFolder = async (folderName: string) => {
    setDialogLoading(true);
    const currentToken = await getToken();

    try {
      await axios.post(
        `${hostname}/documents/folders`,
        {
          name: folderName,
          companyId: companyId,
          path: path,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      refreshData(path);
      setCreateFolderOpen(false);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      throw err;
    } finally {
      setDialogLoading(false);
    }
  };

  const updateFolderTags = async (folderId: string, tags: string[]) => {
    // Prevent updating tags for the shared folder
    if (folderId === "shared") {
      eventListener({
        type: "SET_ERROR",
        error: "Cannot edit tags for the shared folder",
      });
      return;
    }

    setDialogLoading(true);
    const currentToken = await getToken();
    try {
      await axios.put(
        `${hostname}/documents/folders/${folderId}/tags`,
        { tags, companyId },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      refreshData(path);
      eventListener({
        type: "SET_SUCCESS",
        message: "Folder tags updated successfully",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const renameFolder = async (folderId: string, newName: string) => {
    // Prevent renaming special folders
    if (folderId === "shared" || folderId === "recently-deleted") {
      eventListener({
        type: "SET_ERROR",
        error: "Cannot rename special folders",
      });
      return;
    }

    setDialogLoading(true);
    const currentToken = await getToken();
    try {
      await axios.put(
        `${hostname}/documents/folders/${folderId}/rename`,
        { newName, companyId },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      refreshData(path);
      setRenameFolderOpen(false);
      setSelectedFolder(null);
      eventListener({
        type: "SET_SUCCESS",
        message: "Folder renamed successfully",
      });
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || parseError(err);
      eventListener({
        type: "SET_ERROR",
        error: errorMsg,
      });
      throw new Error(errorMsg);
    } finally {
      setDialogLoading(false);
    }
  };

  const restoreDocument = async (documentId: string) => {
    setDialogLoading(true);
    const currentToken = await getToken();
    try {
      await axios.post(
        `${hostname}/documents/${documentId}/restore`,
        {},
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      refreshData(path);
      eventListener({
        type: "SET_SUCCESS",
        message: "Document restored successfully",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setDialogLoading(false);
    }
  };

  const getPathBreadcrumbs = () => {
    if (path == "/") {
      return <link></link>;
    }

    const isExternalPath = path.startsWith("/external/");
    // Filter out "external" from breadcrumbs display - it's just a path prefix, not a real folder
    const displayParts = path.split("/").filter((x) => x && x !== "external");

    return displayParts.map((p, i) => (
        <Link
          component="button"
          key={`${p}-${i}`}
          underline="hover"
          color="inherit"
          onClick={() => {
            // Build the path up to this breadcrumb
            const partsUpToHere = displayParts.slice(0, i + 1);
            let currentPath: string;
            if (isExternalPath) {
              // For external paths, add back the /external prefix
              currentPath = "/external/" + partsUpToHere.join("/");
            } else {
              currentPath = "/" + partsUpToHere.join("/");
            }
            setPath(currentPath);
            refreshData(currentPath);
          }}
        >
          {(i == 0 && p === "shared" && (
            <Typography variant="caption" color="primary">
              {company.name} Shared
            </Typography>
          )) ||
            (i == 0 && p === "recently-deleted" && (
              <Typography variant="caption" color="secondary">
                Recently Deleted
              </Typography>
            )) || <span>{p}</span>}
        </Link>
      ));
  };

  const deleteFolder = async (folder: Document) => {
    const currentToken = await getToken();
    const res = await axios.delete(
      `${hostname}/documents/folders/` + folder.id,
      {
        headers: { Authorization: `Bearer ${currentToken}` },
        params: { companyId },
      }
    );
    const files = res.data.length;
    openDialog(
      "Delete Folder",
      `Are you sure you want to delete ${files} files/folders?`,
      "Confirm",
      () => {
        setRefreshing(true);
        axios
          .delete(`${hostname}/documents/folders/` + folder.id, {
            headers: { Authorization: `Bearer ${currentToken}` },
            params: { delete: true, companyId },
          })
          .then(() => {
            refreshData(path);
          })
          .catch((err) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          });
      }
    );
    return res;
  };

  const rowClick = (row: any) => {
    if (row.type !== "folder" && row.type !== "shared") {
      // For recently deleted documents, show read-only view
      if (path === "/recently-deleted") {
        setNewDocument({ ...row, content: "Loading..." });
        setEditDocumentOpen(true);
        getDocumentContents(row);
      } else {
        setNewDocument({ ...row, content: "Loading..." });
        setEditDocumentOpen(true);
        getDocumentContents(row);
      }
    } else {
      if (row.id === "shared") {
        setPath("/shared");
        refreshData("/shared");
      } else if (row.id === "recently-deleted") {
        setPath("/recently-deleted");
        refreshData("/recently-deleted");
      } else if (row.isExternal && path === "/") {
        // Navigate to an external folder from root - use /external/FolderName path
        setPath("/external/" + row.name);
        refreshData("/external/" + row.name);
      } else if (path == "/") {
        setPath(path + row.name);
        refreshData(path + row.name);
      } else {
        // Navigate into a subfolder (works for both regular and external paths)
        setPath(path + "/" + row.name);
        refreshData(path + "/" + row.name);
      }
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const newCounter = prev - 1;
      if (newCounter === 0) {
        setIsDragging(false);
      }
      return newCounter;
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const processDroppedItems = useCallback(
    async (items: DataTransferItemList) => {
      const entries: FileSystemEntry[] = [];
      for (let i = 0; i < items.length; i++) {
        const entry = items[i].webkitGetAsEntry?.();
        if (entry) {
          entries.push(entry);
        }
      }

      const allFiles: File[] = [];

      const processEntry = async (entry: FileSystemEntry): Promise<void> => {
        if (entry.isFile) {
          const fileEntry = entry as FileSystemFileEntry;
          return new Promise((resolve) => {
            fileEntry.file((file) => {
              const fileExtension =
                "." + file.name.split(".").pop()?.toLowerCase();
              const isValid = ALLOWED_DOCUMENT_EXTENSIONS.includes(
                fileExtension as any
              );

              if (isValid) {
                allFiles.push(file);
              }
              resolve();
            });
          });
        } else if (entry.isDirectory) {
          const dirEntry = entry as FileSystemDirectoryEntry;
          const reader = dirEntry.createReader();
          return new Promise((resolve) => {
            const readEntries = () => {
              reader.readEntries(async (entries) => {
                if (entries.length === 0) {
                  resolve();
                } else {
                  for (const entry of entries) {
                    await processEntry(entry);
                  }
                  readEntries();
                }
              });
            };
            readEntries();
          });
        }
      };

      for (const entry of entries) {
        await processEntry(entry);
      }

      return allFiles;
    },
    []
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setDragCounter(0);

      let validFiles: File[] = [];

      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        validFiles = await processDroppedItems(e.dataTransfer.items);
      } else {
        const files = Array.from(e.dataTransfer.files);
        validFiles = files.filter((file) => {
          const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
          return ALLOWED_DOCUMENT_EXTENSIONS.includes(fileExtension as any);
        });
      }

      if (validFiles.length === 0) {
        eventListener({
          type: "SET_ERROR",
          error: `Please drop valid files (${ALLOWED_DOCUMENT_EXTENSIONS.map(
            (ext) => ext.toUpperCase()
          ).join(", ")})`,
        });
        return;
      }

      setUploadProgress({ current: 0, total: validFiles.length });

      for (let i = 0; i < validFiles.length; i++) {
        setUploadProgress({ current: i + 1, total: validFiles.length });
        await uploadDocumentFile(validFiles[i], []);
      }

      setUploadProgress(null);
    },
    [uploadDocumentFile, eventListener, processDroppedItems]
  );

  if (loading) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  return (
    <Box>
      <SelectEmployeeDialog
        open={selectEmployeesOpen}
        employees={employees}
        title={`Select ${employeeText}`}
        content={`Choose ${employeeLower} to discuss the document.`}
        buttonText="Submit"
        onClose={() => setSelectEmployeesOpen(false)}
        onSubmit={(employee: any) => {
          setSelectEmployeesOpen(false);
          chatWithDocument(employee);
        }}
      />
      <Alert severity="info" sx={{ mt: 1 }}>
        Uploading company documents can be a great way to train your AI{" "}
        {employeesText} in context about your company. The information inside
        the documents can be used to provide additional context to the AI model
        for company relevant answers. Document tags can be used to organize your
        information and provide certain AI {employeesText} with different scopes
        of interest.
      </Alert>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mt: 2,
          mb: 2,
        }}
      >
        <Button
          data-tutorial="upload-document"
          variant="contained"
          onClick={() => setUploadDocumentOpen(true)}
          disabled={folderOperationLoading || path.startsWith("/external/")}
          sx={{
            minWidth: { xs: "100%", sm: "auto" },
          }}
        >
          <UploadFileIcon style={{ marginRight: 3 }} /> Upload Document
        </Button>
        <Button
          data-tutorial="create-document"
          variant="outlined"
          onClick={() => setCreateDocumentOpen(true)}
          disabled={folderOperationLoading || path.startsWith("/external/")}
          sx={{
            minWidth: { xs: "100%", sm: "auto" },
          }}
        >
          <PostAddIcon style={{ marginRight: 3 }} /> Create Document
        </Button>
        <Button
          data-tutorial="create-folder"
          variant="outlined"
          onClick={() => setCreateFolderOpen(true)}
          disabled={folderOperationLoading || path.startsWith("/external/")}
          sx={{
            minWidth: { xs: "100%", sm: "auto" },
          }}
        >
          <CreateNewFolderIcon style={{ marginRight: 4 }} /> Create Folder
        </Button>
        <Button
          data-tutorial="import-website"
          variant="outlined"
          onClick={() => {
            setCrawlWebsiteOpen(true);
          }}
          disabled={folderOperationLoading || path.startsWith("/external/")}
          sx={{
            minWidth: { xs: "100%", sm: "auto" },
          }}
        >
          <TravelExploreIcon style={{ marginRight: 3 }} /> Import Website
        </Button>
        {!isFeatureHidden(FEATURES.INTEGRATIONS) && (
          <Button
            data-tutorial="sync-integration"
            variant="outlined"
            onClick={() => {
              setTab("integrations");
            }}
            disabled={isFreeUser(currentPlan) || folderOperationLoading}
            sx={{
              minWidth: { xs: "100%", sm: "auto" },
            }}
          >
            <SyncIcon style={{ marginRight: 3 }} />
            Sync Settings
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={() => setManageTagsOpen(true)}
          disabled={folderOperationLoading}
          sx={{
            minWidth: { xs: "100%", sm: "auto" },
          }}
        >
          <LocalOfferIcon style={{ marginRight: 3 }} />
          Manage Tags
        </Button>
        {isAdmin && (
          <SharedFoldersButton
            companyId={companyId}
            getToken={getToken}
            onClick={() => setSharedFoldersDialogOpen(true)}
            refreshKey={statusBarRefreshKey}
            disabled={folderOperationLoading}
          />
        )}
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          freeSolo
          open={searchOpen && searchOptions.length > 0}
          onOpen={() => setSearchOpen(true)}
          onClose={() => setSearchOpen(false)}
          options={searchOptions}
          loading={isSearching}
          inputValue={searchInputValue}
          onInputChange={(_event, newInputValue) => {
            setSearchInputValue(newInputValue);

            // Clear any pending debounce
            if (searchDebounceRef.current) {
              clearTimeout(searchDebounceRef.current);
            }

            if (newInputValue.length >= 2) {
              // Debounce search by 500ms
              searchDebounceRef.current = setTimeout(() => {
                searchDocuments(newInputValue);
              }, 500);
            } else {
              setSearchOptions([]);
            }
          }}
          onChange={handleSearchSelect}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.name || ""
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(x) => x}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            const fullPath =
              option.path === "/"
                ? `/${option.name}`
                : `${option.path}/${option.name}`;
            return (
              <li key={option.id} {...otherProps}>
                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                  {option.type === "folder" ? (
                    <SourceIcon color="primary" sx={{ mr: 1.5, fontSize: 20 }} />
                  ) : (
                    <Description color="action" sx={{ mr: 1.5, fontSize: 20 }} />
                  )}
                  <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
                    <Typography variant="body2" noWrap>
                      {option.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: "block" }}
                    >
                      {fullPath}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={(option as any).matchType || "name"}
                    sx={{ ml: 1, fontSize: "0.65rem", height: 20 }}
                    variant="outlined"
                  />
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Search documents by name, content, or tags..."
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {isSearching ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      {/* Drop Zone */}
      <Box
        sx={{
          mt: 2,
          mb: 2,
          py: 1.5,
          px: 2,
          border: "1px dashed",
          borderColor: isDragging ? "primary.main" : "divider",
          backgroundColor: isDragging
            ? "rgba(25, 118, 210, 0.08)"
            : "transparent",
          textAlign: "center",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          borderRadius: 1,
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "rgba(25, 118, 210, 0.04)",
          },
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => setUploadDocumentOpen(true)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CloudUploadIcon
            sx={{
              fontSize: 24,
              color: isDragging ? "primary.main" : "text.secondary",
            }}
          />
          <Box sx={{ textAlign: "left" }}>
            <Typography
              variant="body1"
              color={isDragging ? "primary.main" : "text.primary"}
              sx={{ fontWeight: 500 }}
            >
              {isDragging
                ? "Drop files here to upload"
                : "Drag and drop files here or click to browse"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {`Supported: ${ALLOWED_DOCUMENT_EXTENSIONS.map((ext) =>
                ext.toUpperCase()
              ).join(", ")}`}
            </Typography>
          </Box>
        </Box>
        {uploadProgress && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="primary">
              Uploading file {uploadProgress.current} of {uploadProgress.total}
              ...
            </Typography>
          </Box>
        )}
      </Box>

      <Paper
        style={{ marginTop: 20, marginBottom: 40, position: "relative" }}
        variant="outlined"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {(refreshing || uploadProgress) && <LinearProgress />}
        {!refreshing && !uploadProgress && <div style={{ height: 4 }}></div>}
        <div style={{ display: "flex" }}>
          <Breadcrumbs
            aria-label="breadcrumb"
            style={{ marginLeft: 10, marginTop: 8, flexGrow: 1 }}
          >
            <Link
              underline="hover"
              color="inherit"
              component="button"
              onClick={() => {
                setPath("/");
                refreshData("/");
              }}
            >
              Root
            </Link>
            {getPathBreadcrumbs()}
          </Breadcrumbs>
          <IconButton
            onClick={() => refreshData(path)}
            disabled={folderOperationLoading}
            style={{ float: "right", marginRight: 3 }}
          >
            <RefreshIcon />
          </IconButton>
        </div>
        <TableContainer data-tutorial="document-list">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                <TableCell component="th" scope="row" sortDirection="desc">
                  <TableSortLabel active={true} direction="desc">
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Sharing</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((row) => (
                <TableRow
                  sx={{
                    cursor: folderOperationLoading ? "not-allowed" : "pointer",
                    opacity: folderOperationLoading ? 0.6 : 1,
                  }}
                  key={row.id}
                  onClick={() => !folderOperationLoading && rowClick(row)}
                >
                  <TableCell>
                    {(() => {
                      const chipProps = getSharingChipProps(row.shareVisibility, row.sharing, row.isExternal);
                      const iconColor = chipProps.color === "default" ? "inherit" : chipProps.color;

                      if (row.id === "recently-deleted") {
                        return <DeleteOutline color="primary" style={{ marginRight: 10 }} />;
                      }
                      if (row.isExternal && row.type === "folder") {
                        return <SourceIcon color="warning" style={{ marginRight: 10 }} />;
                      }
                      if (row.isExternal) {
                        return <Description color="warning" style={{ marginRight: 10 }} />;
                      }
                      if (row.type === "folder" || row.id === "shared") {
                        return <SourceIcon color={iconColor} style={{ marginRight: 10 }} />;
                      }
                      return <Description color={iconColor} style={{ marginRight: 10 }} />;
                    })()}
                  </TableCell>
                  <TableCell>
                    {row.id === "shared" ? `${company.name} Shared` : row.name}
                    {row.ownerCompany && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({row.ownerCompany.name})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.type}</TableCell>
                  <TableCell>
                    {(() => {
                      const chipProps = getSharingChipProps(row.shareVisibility, row.sharing, row.isExternal);
                      return (
                        <Tooltip title={chipProps.tooltip} arrow>
                          <Chip
                            icon={chipProps.icon}
                            label={chipProps.label}
                            color={chipProps.color}
                            size="small"
                          />
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell>{row.tokens || "--"}</TableCell>
                  <TableCell>
                    {formatTimestamp(row.created).fullDateTime || "--"}
                  </TableCell>
                  <TableCell>
                    {formatTimestamp(row.updated).fullDateTime || "--"}
                  </TableCell>
                  <TableCell>
                    {row.type === "folder" ? (
                      // Folder: show folderTags (auto-apply) and childTags (from children)
                      <>
                        {(!row.folderTags || row.folderTags.length === 0) &&
                          (!row.childTags || row.childTags.length === 0) && (
                            <span>--</span>
                          )}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {row.folderTags?.map((tag) => (
                            <Tooltip
                              key={`folder-${tag}`}
                              title="Auto-applies to all items in this folder"
                            >
                              <Chip
                                label={tag}
                                size="small"
                                color="primary"
                                variant="filled"
                                icon={<LocalOfferIcon />}
                              />
                            </Tooltip>
                          ))}
                          {row.childTags
                            ?.filter((tag) => !row.folderTags?.includes(tag))
                            .map((tag) => (
                              <Tooltip
                                key={`child-${tag}`}
                                title="Tag from documents in this folder"
                              >
                                <Chip
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            ))}
                        </Stack>
                      </>
                    ) : (
                      // Document: show tags (own) and inheritedTags (from parent folders)
                      <>
                        {(!row.tags || row.tags.length === 0) &&
                          (!row.inheritedTags || row.inheritedTags.length === 0) && (
                            <span>--</span>
                          )}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {row.inheritedTags?.map((tag) => (
                            <Tooltip
                              key={`inherited-${tag}`}
                              title="Inherited from parent folder"
                            >
                              <Chip
                                label={tag}
                                size="small"
                                color="primary"
                                variant="filled"
                                icon={<LocalOfferIcon />}
                              />
                            </Tooltip>
                          ))}
                          {row.tags
                            ?.filter((tag) => !row.inheritedTags?.includes(tag))
                            .map((tag) => (
                              <Chip key={tag} label={tag} size="small" />
                            ))}
                        </Stack>
                      </>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {row.type === "folder" && row.id !== "shared" && row.id !== "recently-deleted" && !row.isExternal && (
                      <span>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!folderOperationLoading) {
                              setOpenButton(row.id);
                              setAnchorEl(e.currentTarget);
                            }
                          }}
                          disabled={folderOperationLoading}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={row.id === openButton}
                          onClose={(e: any) => {
                            setOpenButton("");
                            e.stopPropagation();
                          }}
                        >
                          {!row.isCompanyShared && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFolder(row);
                                setRenameFolderOpen(true);
                                setOpenButton("");
                              }}
                            >
                              Rename
                            </MenuItem>
                          )}
                          {!row.isExternal && (
                            <Tooltip
                              title={
                                row.shareVisibility
                                  ? "Remove sharing before moving this folder"
                                  : ""
                              }
                            >
                              <span>
                                <MenuItem
                                  disabled={!!row.shareVisibility}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMoveFolderId(row.id);
                                    setFolderSelectorOpen(true);
                                    setOpenButton("");
                                  }}
                                >
                                  Move
                                </MenuItem>
                              </span>
                            </Tooltip>
                          )}
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFolder(row);
                              setEditFolderTagsOpen(true);
                              setOpenButton("");
                            }}
                          >
                            Edit Tags
                          </MenuItem>
                          {isAdmin && !row.public && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFolder(row);
                                setShareFolderOpen(true);
                                setOpenButton("");
                              }}
                            >
                              Edit Sharing
                            </MenuItem>
                          )}
                          {!row.isCompanyShared && !row.isExternal && (
                            <MenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedFolder(row);
                                setTransferFolderOpen(true);
                                setOpenButton("");
                              }}
                            >
                              Transfer
                            </MenuItem>
                          )}
                          <MenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                                deleteFolder(row);
                                setOpenButton("");
                              }}
                            >
                              Delete
                            </MenuItem>
                        </Menu>
                      </span>
                    )}
                    {row.type !== "folder" && row.type !== "shared" && !row.isExternal && (
                      <span>
                        <IconButton
                          onClick={(e: any) => {
                            e.stopPropagation();
                            if (!folderOperationLoading) {
                              setOpenButton(row.id);
                              setAnchorEl(e.currentTarget);
                            }
                          }}
                          disabled={folderOperationLoading}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={anchorEl}
                          open={row.id === openButton}
                          onClose={(e: any) => {
                            setOpenButton("");
                            e.stopPropagation();
                          }}
                        >
                          {path === "/recently-deleted" ? (
                            // Actions for recently deleted documents
                            <>
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  restoreDocument(row.id);
                                  setOpenButton("");
                                }}
                              >
                                Restore
                              </MenuItem>
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNewDocument({
                                    ...row,
                                    content: "Loading...",
                                  });
                                  setEditDocumentOpen(true);
                                  getDocumentContents(row);
                                  setOpenButton("");
                                }}
                              >
                                View (Read Only)
                              </MenuItem>
                            </>
                          ) : (
                            // Actions for regular documents
                            <>
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNewDocument({
                                    ...row,
                                    content: "Loading...",
                                  });
                                  setEditDocumentOpen(true);
                                  getDocumentContents(row);
                                  setOpenButton("");
                                }}
                              >
                                Edit
                              </MenuItem>
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMoveFileId(row.id);
                                  setIsMovingFile(true);
                                  setFolderSelectorOpen(true);
                                  setOpenButton("");
                                }}
                              >
                                Move
                              </MenuItem>
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCopyFileId(row.id);
                                  setIsMovingFile(false);
                                  setFolderSelectorOpen(true);
                                  setOpenButton("");
                                }}
                              >
                                Copy
                              </MenuItem>
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNewDocument({
                                    ...row,
                                  });
                                  setSelectEmployeesOpen(true);
                                  setOpenButton("");
                                }}
                              >
                                Chat with Document
                              </MenuItem>
                              <MenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteDocument(row);
                                  setOpenButton("");
                                }}
                              >
                                Delete
                              </MenuItem>
                            </>
                          )}
                        </Menu>
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {isDragging && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(25, 118, 210, 0.08)",
              border: "2px dashed",
              borderColor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 60, color: "primary.main" }} />
              <Typography variant="h5" color="primary">
                Drop files here to upload
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`Supported formats: ${ALLOWED_DOCUMENT_EXTENSIONS.map((ext) =>
                  ext.toUpperCase()
                ).join(", ")}`}
              </Typography>
              {uploadProgress && (
                <Typography variant="body2" color="primary">
                  Uploading file {uploadProgress.current} of{" "}
                  {uploadProgress.total}...
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Paper>
      {documents.length === 0 && !loading && (
        <Box
          sx={{
            mt: 4,
            p: 4,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 2,
            textAlign: "center",
            backgroundColor: isDragging
              ? "rgba(25, 118, 210, 0.08)"
              : "transparent",
            transition: "background-color 0.2s",
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <CloudUploadIcon
            sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
          />
          <Typography variant="h6" gutterBottom>
            Drag and drop files here
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            or use the buttons above to upload documents
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`Supported formats: ${ALLOWED_DOCUMENT_EXTENSIONS.map((ext) =>
              ext.toUpperCase()
            ).join(", ")}`}
          </Typography>
        </Box>
      )}
      <WebsiteCrawlerDialog
        open={crawlWebsiteOpen}
        onClose={() => setCrawlWebsiteOpen(false)}
        getToken={getToken}
        companyId={companyId}
        company={company}
        eventListener={eventListener}
      />
      <CreateFolderDialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        onCreateFolder={createFolder}
        loading={dialogLoading}
      />
      <CreateDocumentDialog
        open={createDocumentOpen}
        onClose={() => setCreateDocumentOpen(false)}
        onCreateDocument={createDocumentFile}
        availableTags={tags}
        loading={dialogLoading}
        companyId={companyId}
        getToken={getToken}
      />
      <EditDocumentDialog
        open={editDocumentOpen}
        onClose={() => setEditDocumentOpen(false)}
        onSaveDocument={editDocument}
        document={newDocument}
        availableTags={tags}
        loading={dialogLoading}
        companyId={companyId}
        getToken={getToken}
      />
      <UploadDocumentDialog
        open={uploadDocumentOpen}
        onClose={() => setUploadDocumentOpen(false)}
        onUploadDocument={uploadDocumentFile}
        availableTags={tags}
        selectedFolderId={path}
        loading={dialogLoading}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
      />
      <FolderSelector
        open={folderSelectorOpen}
        getToken={getToken}
        company={company}
        onClick={(folder: any) =>
          moveFolderId ? moveFolder(folder) : moveFile(folder)
        }
        onClose={() => {
          setFolderSelectorOpen(false);
          setMoveFolderId("");
        }}
        eventListener={eventListener}
        title={
          moveFolderId
            ? "Move Folder"
            : isMovingFile
              ? "Move Document to Folder"
              : "Copy Document to Folder"
        }
        operationLoading={folderOperationLoading}
      />

      {/* Loading backdrop for folder operations */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: "column",
          gap: 2,
        }}
        open={folderOperationLoading && !folderSelectorOpen}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">
          {moveFolderId
            ? "Moving folder..."
            : isMovingFile
              ? "Moving document..."
              : "Copying document..."}
        </Typography>
      </Backdrop>

      <EditFolderTagsDialog
        open={editFolderTagsOpen}
        onClose={() => {
          setEditFolderTagsOpen(false);
          setSelectedFolder(null);
        }}
        folder={selectedFolder}
        onUpdateTags={updateFolderTags}
        loading={dialogLoading}
        companyId={companyId}
        getToken={getToken}
      />

      <ManageTagsDialog
        open={manageTagsOpen}
        onClose={() => setManageTagsOpen(false)}
        companyId={companyId}
        getToken={getToken}
        eventListener={eventListener}
      />

      <ShareFolderDialog
        open={shareFolderOpen}
        onClose={() => {
          setShareFolderOpen(false);
          setSelectedFolder(null);
        }}
        folder={selectedFolder}
        companyId={companyId}
        getToken={getToken}
        onShareCreated={() => {
          setStatusBarRefreshKey((prev) => prev + 1);
          refreshData(path);
        }}
        userRole={userRole}
      />

      <SharedFoldersDialog
        open={sharedFoldersDialogOpen}
        onClose={() => {
          setSharedFoldersDialogOpen(false);
          setStatusBarRefreshKey((prev) => prev + 1);
        }}
        companyId={companyId}
        getToken={getToken}
      />

      <RenameFolderDialog
        open={renameFolderOpen}
        onClose={() => {
          setRenameFolderOpen(false);
          setSelectedFolder(null);
        }}
        folder={selectedFolder}
        onRenameFolder={renameFolder}
        loading={dialogLoading}
      />

      <TransferFolderDialog
        open={transferFolderOpen}
        onClose={() => {
          setTransferFolderOpen(false);
          setSelectedFolder(null);
        }}
        folder={selectedFolder}
        companyId={companyId}
        currentUserUid={currentUser.uid}
        getToken={getToken}
        onTransferComplete={() => {
          refreshData(path);
        }}
      />
    </Box>
  );
};
